import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import SearchableDropdown from "../components/SearchableDropdown";
import { ProductProfitabilityReportTable } from "../components/ReportTable";
import { productProfitabilityColumns } from "../config/reportColumns";
import {
  fetchProductProfitDropdowns,
  fetchProductProfitReport,
} from "../services/productProfitabilityReport";
import ExcelExport from "../components/ExcelExport";

const ProductProfitabilityReport = ({ onLogout }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* =========================
     DROPDOWNS
  ========================== */
  const [productList, setProductList] = useState([]);
  const [godownList, setGodownList] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedGodown, setSelectedGodown] = useState([]);

  const isFetchingRef = useRef(false);

  /* =========================
     LOAD DROPDOWNS + DEFAULT DATA
  ========================== */
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        const [dropdownRes, reportRes] = await Promise.all([
          fetchProductProfitDropdowns(),
          fetchProductProfitReport()
        ]);

        console.log("Raw Profit Dropdowns:", dropdownRes);

        // FIXED: The PHP returns products and godowns directly as arrays of strings
        // Not nested under data or any other key
        const payload = dropdownRes || {};
        
        // Get products array - if it exists, use it, otherwise empty array
        const pArr = Array.isArray(payload.products) ? payload.products : [];
        const gArr = Array.isArray(payload.godowns) ? payload.godowns : [];

        // Format options for SearchableDropdown - expects {value, label} objects
        const formatOption = (item) => {
          // If it's already an object with value/label, return as is
          if (typeof item === 'object' && item !== null && item.value !== undefined) {
            return item;
          }
          // If it's a string, create value/label object
          if (typeof item === 'string') {
            return { value: item, label: item };
          }
          // Fallback: try to extract value from object
          if (typeof item === 'object' && item !== null) {
            const values = Object.values(item);
            if (values.length > 0) {
              const val = values[0];
              return { value: val, label: String(val) };
            }
          }
          return { value: item, label: String(item) };
        };

        setProductList(pArr.map(formatOption));
        setGodownList(gArr.map(formatOption));

        // Handle report data - check if it's nested in data or directly the array
        let reportDataArray = [];
        if (reportRes?.data && Array.isArray(reportRes.data)) {
          reportDataArray = reportRes.data;
        } else if (Array.isArray(reportRes)) {
          reportDataArray = reportRes;
        } else {
          reportDataArray = [];
        }

        setReportData(reportDataArray.map((row, i) => ({ ...row, sl: i + 1 })));
      } catch (err) {
        console.error("Error loading initial data:", err);
        setError("Failed to load report");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  /* =========================
     FILTER HANDLER
  ========================== */
  const loadFilteredData = async ({
    product = selectedProduct,
    godown = selectedGodown,
  } = {}) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Extract values from the selected objects
      const productValues = Array.isArray(product) 
        ? product.map(item => typeof item === 'object' ? item.value : item).join(',')
        : '';
      
      const godownValues = Array.isArray(godown)
        ? godown.map(item => typeof item === 'object' ? item.value : item).join(',')
        : '';

      const res = await fetchProductProfitReport({
        product: productValues,
        godown: godownValues
      });
      
      // Handle response data structure
      let reportDataArray = [];
      if (res?.data && Array.isArray(res.data)) {
        reportDataArray = res.data;
      } else if (Array.isArray(res)) {
        reportDataArray = res;
      } else {
        reportDataArray = [];
      }
      
      setReportData(
        reportDataArray.map((row, i) => ({
          ...row,
          sl: i + 1,
        }))
      );
    } catch (err) {
      console.error("Error loading filtered data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  return (
    <DashboardLayout onLogout={onLogout}>
      <div className="flex flex-col h-screen w-full bg-gray-100 overflow-hidden">

        {/* HEADER */}
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-800">
            Product Profitability Report
          </h1>

          <ExcelExport
            data={reportData}
            columns={productProfitabilityColumns}
            reportTitle="Product Profitability Report"
            fileName="Product_Profitability_Report.xlsx"
            reportFilters={{
              product: selectedProduct,
              godown: selectedGodown,
            }}
            groupKeyFields={["godown_name", "product_name"]}
            hiddenRepeatKeys={["total_quantity", "godown_name", "total_amount", "total_consumption", "total_profit", "product_name"]}
          />
        </div>

        {/* FILTER BAR */}
        <div className="bg-white px-4 py-3 border-b flex gap-4 flex-wrap">

          {/* PRODUCT */}
          <SearchableDropdown
            label="Product"
            options={productList}
            value={selectedProduct}
            onChange={(val) => {
              const valArray = val || [];
              setSelectedProduct(valArray);
              loadFilteredData({ product: valArray });
            }}
            placeholder="Select Product"
            isMulti={true}
          />

          {/* GODOWN */}
          <SearchableDropdown
            label="Godown"
            options={godownList}
            value={selectedGodown}
            onChange={(val) => {
              const valArray = val || [];
              setSelectedGodown(valArray);
              loadFilteredData({ godown: valArray });
            }}
            placeholder="Select Godown"
            isMulti={true}
          />

        </div>

        {/* TABLE */}
        <div className="flex-1 overflow-auto bg-white">
          {loading && (
            <div className="text-center py-20 text-gray-500">
              Fetching records...
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-10 text-red-500">
              {error}
            </div>
          )}

          {!loading && !error && reportData.length === 0 && (
            <div className="text-center py-10 text-gray-500">
              No data available
            </div>
          )}

          {!loading && !error && reportData.length > 0 && (
            <ProductProfitabilityReportTable
              columns={productProfitabilityColumns}
              data={reportData}
            />
          )}
        </div>

      </div>
    </DashboardLayout>
  );
};

export default ProductProfitabilityReport;